// src/pages/Community.js
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Users, Sparkles, Sword, Crown, MessageCircle, BookOpen, Trophy, ChevronRight, X, Instagram, Twitter, Calendar } from 'lucide-react';
import SEO from '../components/SEO';

// Shared PageStyles component for consistent styling
const PageStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Azonix&display=swap');
    
    :root {
      --az: #a78bfa;
      --jp: #fbbf24;
      --wt: #06b6d4;
      --neu: #8b5cf6;
      --bg: #08080c;
      --glass: rgba(255, 255, 255, 0.03);
      --gb: rgba(167, 139, 250, 0.15);
      --card-bg: linear-gradient(135deg, rgba(20, 20, 30, 0.9), rgba(10, 10, 15, 0.95));
      --accent-gradient: linear-gradient(135deg, #a78bfa 0%, #6366f1 50%, #8b5cf6 100%);
    }
    
    * {
      scrollbar-width: thin;
      scrollbar-color: var(--az) var(--bg);
    }
    
    *::-webkit-scrollbar {
      width: 8px;
      height: 8px;
    }
    
    *::-webkit-scrollbar-track {
      background: var(--bg);
    }
    
    *::-webkit-scrollbar-thumb {
      background: var(--az);
      border-radius: 4px;
    }
    
    .font-azonix {
      font-family: 'Azonix', sans-serif;
    }
    
    .glass-card {
      background: var(--glass);
      backdrop-filter: blur(20px);
      border: 1px solid var(--gb);
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.05);
    }
    
    .glow-border {
      position: relative;
    }
    
    .glow-border::before {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: inherit;
      padding: 1px;
      background: linear-gradient(135deg, rgba(167, 139, 250, 0.5), rgba(99, 102, 241, 0.3), rgba(139, 92, 246, 0.5));
      -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
      mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
      -webkit-mask-composite: xor;
      mask-composite: exclude;
      pointer-events: none;
    }
    
    .gradient-text {
      background: linear-gradient(135deg, #a78bfa 0%, #6366f1 50%, #8b5cf6 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
    
    .gradient-glow {
      background: linear-gradient(135deg, rgba(167, 139, 250, 0.1) 0%, rgba(99, 102, 241, 0.05) 100%);
    }
  `}</style>
);

export default function Community() {
  const [activeSlide, setActiveSlide] = useState(0);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [learnMoreOpen, setLearnMoreOpen] = useState(false);
  const [joinNowOpen, setJoinNowOpen] = useState(false);

  // Slide-specific modal content
  const slideContent = [
    {
      title: 'Anime & Manga',
      description: 'Dive deep into the world of Japanese anime and manga! From seasonal favorites to hidden gems, discuss the latest releases, share recommendations, and connect with fellow enthusiasts.',
      features: ['Seasonal Anime Reviews', 'Manga Discussions', 'Cosplay Showcase', 'Anime News & Updates', 'Watch Party Planning'],
      joinLink: 'https://chat.whatsapp.com/E7nbWo1ZXocA03OQnXRqHf'
    },
    {
      title: 'Movies & TV',
      description: 'Your hub for Hollywood blockbusters, streaming series, and all things Western entertainment! From Marvel to Netflix, discuss your favorite shows and movies.',
      features: ['Movie Reviews', 'TV Show Discussions', 'Streaming Guides', 'Industry News', 'Watch Party Planning'],
      joinLink: 'https://chat.whatsapp.com/BLRRMo1M4Q58M26cf7lP1n'
    },
    {
      title: 'Gaming & RPG',
      description: 'Competitive gaming meets epic roleplay adventures! Join tournaments, discuss your favorite games, and immerse yourself in RPG storylines.',
      features: ['Tournament Events', 'RPG Discussions', 'Gaming News', 'Clan Recruitment', 'Game Recommendations'],
      joinLink: 'https://chat.whatsapp.com/LxTqWURqy231Tkof0tvKSu'
    },
    {
      title: 'Live Events',
      description: 'Watch parties, Q&As, and exclusive community events! Stay connected with everything happening in the ANIMAC universe.',
      features: ['Watch Parties', 'Community Q&As', 'Exclusive Events', 'Event Announcements', 'Behind the Scenes'],
      joinLink: 'https://chat.whatsapp.com/JpqX0QqmB9p8LXlZQKXtYb'
    }
  ];

  const mainCommunityLink = 'https://chat.whatsapp.com/Hiy0AnYBfQM97meXDVIt1V';

  // Hero carousel images
  const carouselImages = [
    { src: 'https://i.pinimg.com/1200x/11/ce/f5/11cef5593997078db2b6ee9b21061581.jpg', alt: 'Anime Community', title: 'Anime & Manga', subtitle: 'Discover the latest in Japanese entertainment' },
    { src: 'https://i.pinimg.com/736x/7a/6e/1b/7a6e1b6e54c60109935ac235f10055dc.jpg', alt: 'Movie Enthusiasts', title: 'Movies & TV', subtitle: 'Hollywood blockbusters and streaming series' },
    { src: 'https://i.pinimg.com/1200x/17/cf/72/17cf72663ad6e3d1c23db253b01fe6dd.jpg', alt: 'Gaming Community', title: 'Gaming & RPG', subtitle: 'Competitive gaming and roleplay adventures' },
    { src: 'https://i.pinimg.com/1200x/30/a8/0a/30a80ac20aedc636d3a2152250bcf09b.jpg', alt: 'Community Events', title: 'Live Events', subtitle: 'Watch parties, Q&As, and more' },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % carouselImages.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [carouselImages.length]);

  const communityGroups = [
    {
      id: 'east-gate',
      name: 'East Gate',
      tagline: 'Eastern Content Hub',
      description: 'Your gateway to Japanese anime, Korean dramas, Chinese media, and all things Asian entertainment. Dive deep into seasonal anime reviews, manga recommendations, and cultural discussions.',
      icon: Crown,
      stats: { members: '8,500+', posts: '45K+', online: '320+' },
      features: ['Seasonal Anime Reviews', 'Manga Discussions', 'Korean Drama Hub', 'Japanese Culture Talk', 'Cosplay Showcase'],
      gradient: 'from-red-600 via-orange-500 to-yellow-490',
      accent: 'text-orange-400',
      border: 'border-orange-500/30',
      bgPattern: '🌸'
    },
    {
      id: 'west-gate',
      name: 'West Gate',
      tagline: 'Western Entertainment',
      description: 'Explore Hollywood blockbusters, indie films, streaming series, and Western animation. From Marvel to Netflix, discuss everything in Western entertainment.',
      icon: Sparkles,
      stats: { members: '6,200+', posts: '38K+', online: '280+' },
      features: ['Movie Reviews', 'TV Show Discussions', 'Streaming Guides', 'Western Animation', 'Industry News'],
      gradient: 'from-blue-600 via-cyan-500 to-teal-500',
      accent: 'text-cyan-400',
      border: 'border-cyan-500/30',
      bgPattern: '🎬'
    },
    {
      id: 'burst-knights',
      name: 'Burst Knights',
      tagline: 'RPG Roleplay System',
      description: 'An immersive RPG experience with unique gameplay mechanics, character progression, clan battles, and epic storylines. Create your knight and forge your destiny.',
      icon: Sword,
      stats: { members: '3,800+', posts: '28K+', online: '450+' },
      features: ['Character Creation', 'Clan Wars', 'Quest System', 'Item Trading', 'Tournament Events'],
      gradient: 'from-purple-600 via-violet-500 to-indigo-500',
      accent: 'text-violet-400',
      border: 'border-violet-500/30',
      bgPattern: '⚔️'
    },
    {
      id: 'memes',
      name: 'Meme Lounge',
      tagline: 'Entertainment & Humor',
      description: 'The fun side of the community! Share memes, jokes, shitposts, and humorous content. Light-hearted discussions and daily entertainment guaranteed.',
      icon: MessageCircle,
      stats: { members: '12,000+', posts: '85K+', online: '890+' },
      features: ['Meme Sharing', 'Daily Challenges', 'Comedy Threads', 'OC Memes', 'Reaction Images'],
      gradient: 'from-pink-600 via-rose-500 to-red-500',
      accent: 'text-rose-400',
      border: 'border-rose-500/30',
      bgPattern: '😂'
    },
    {
      id: 'comics',
      name: 'Comics & Light Novels',
      tagline: 'Sequential Art & Reading',
      description: 'For fans of comics, graphic novels, manga, and light novels. In-depth discussions, reviews, recommendations, and reading groups.',
      icon: BookOpen,
      stats: { members: '4,500+', posts: '32K+', online: '180+' },
      features: ['Manga Reviews', 'Comic Discussions', 'Reading Groups', 'Author Spotlights', 'Collection Showcases'],
      gradient: 'from-emerald-600 via-green-500 to-teal-500',
      accent: 'text-emerald-400',
      border: 'border-emerald-500/30',
      bgPattern: '📚'
    },
    {
      id: 'tournaments',
      name: 'Arena',
      tagline: 'Debates & Tournaments',
      description: 'Competitive arena for structured debates, gaming tournaments, and skill competitions. Prove your knowledge and claim victory.',
      icon: Trophy,
      stats: { members: '2,800+', posts: '15K+', online: '220+' },
      features: ['Debate Events', 'Gaming Tournaments', 'Trivia Nights', 'Prediction Leagues', 'Prize Pool'],
      gradient: 'from-amber-600 via-orange-500 to-yellow-500',
      accent: 'text-amber-400',
      border: 'border-amber-500/30',
      bgPattern: '🏆'
    }
  ];

  return (
    <>
      <PageStyles />
      <SEO
        title="Community - ANIMAC Entertainment Hub"
        description="Join ANIMAC's vibrant community. Connect with fans across East Gate, West Gate, Burst Knights RPG, Meme Lounge, Comics & Light Novels, and competitive Arena."
        url="/community"
        image="/assets/animac-preview-logo.svg"
        type="website"
      />

      <div className="min-h-screen relative overflow-hidden" style={{ background: '#08080c' }}>
        {/* Background effects */}
        <div className="absolute inset-0 gradient-glow"></div>
        <div className="absolute inset-0" style={{
          backgroundImage: `
            radial-gradient(circle at 20% 80%, rgba(167, 139, 250, 0.08) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(99, 102, 241, 0.08) 0%, transparent 50%),
            radial-gradient(circle at 50% 50%, rgba(139, 92, 246, 0.05) 0%, transparent 70%)
          `
        }}></div>

        {/* Hero Section with Dynamic Carousel */}
        <section className="relative h-[85vh] min-h-[600px]">
          {/* Carousel Background */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeSlide}
              initial={{ opacity: 0, scale: 1.1 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.2, ease: "easeOut" }}
              className="absolute inset-0"
            >
              <div 
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url(${carouselImages[activeSlide].src})` }}
              />
              {/* Gradient Overlays */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#08080c] via-[rgba(8,8,12,0.6)] to-[rgba(8,8,12,0.3)]" />
              <div className="absolute inset-0 bg-gradient-to-r from-[rgba(8,8,12,0.8)] via-transparent to-transparent" />
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[rgba(8,8,12,0.2)] to-[#08080c]" />
            </motion.div>
          </AnimatePresence>

          {/* Animated Background Elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <motion.div
              animate={{ 
                scale: [1, 1.2, 1],
                opacity: [0.3, 0.5, 0.3]
              }}
              transition={{ duration: 8, repeat: Infinity }}
              className="absolute top-1/4 -left-20 w-96 h-96 bg-[#a78bfa]/20 rounded-full blur-[120px]"
            />
            <motion.div
              animate={{ 
                scale: [1.2, 1, 1.2],
                opacity: [0.2, 0.4, 0.2]
              }}
              transition={{ duration: 10, repeat: Infinity }}
              className="absolute bottom-1/4 -right-20 w-80 h-80 bg-[#8b5cf6]/20 rounded-full blur-[100px]"
            />
          </div>

          {/* Carousel Indicators */}
          <div className="absolute bottom-32 left-1/2 -translate-x-1/2 flex gap-3 z-20">
            {carouselImages.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setActiveSlide(idx)}
                className={`transition-all duration-500 ${
                  idx === activeSlide 
                    ? 'w-12 bg-[#a78bfa]/80' 
                    : 'w-3 bg-white/30 hover:bg-[#a78bfa]/50'
                } h-1 rounded-full`}
              />
            ))}
          </div>

          {/* Hero Content */}
          <div className="relative z-20 h-full flex items-center">
            <div className="container mx-auto px-6 lg:px-12">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeSlide}
                  initial={{ opacity: 0, y: 40 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.8, delay: 0.3 }}
                  className="max-w-3xl"
                >
                  <motion.span 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                    className="inline-block px-4 py-1.5 glass-card backdrop-blur-md border border-[rgba(167,139,250,0.3)] rounded-full text-sm text-[#a78bfa] mb-6"
                  >
                    Welcome to ANIMAC
                  </motion.span>
                  <motion.h1 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                    className="text-5xl md:text-7xl font-azonix font-bold mb-4 leading-tight"
                  >
                    <span className="gradient-text">
                      {carouselImages[activeSlide].title}
                    </span>
                  </motion.h1>
                  <motion.p 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7 }}
                    className="text-xl md:text-2xl text-gray-300 mb-8 font-light"
                  >
                    {carouselImages[activeSlide].subtitle}
                  </motion.p>
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8 }}
                    className="flex flex-wrap gap-4"
                  >
                    <button 
                      onClick={() => setJoinNowOpen(true)}
                      className="px-8 py-3.5 glass-card text-white font-semibold rounded-lg hover:bg-[rgba(167,139,250,0.2)] transition-colors flex items-center gap-2 glow-border"
                    >
                      Join Now <ChevronRight size={20} />
                    </button>
                    <button 
                      onClick={() => setLearnMoreOpen(true)}
                      className="px-8 py-3.5 glass-card text-white font-semibold rounded-lg hover:bg-[rgba(167,139,250,0.1)] transition-colors border border-[rgba(167,139,250,0.3)]"
                    >
                      Learn More
                    </button>
                  </motion.div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          {/* Scroll Indicator */}
          <motion.div 
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="absolute bottom-8 left-1/2 -translate-x-1/2 text-gray-400"
          >
            <div className="w-6 h-10 border-2 border-[rgba(167,139,250,0.3)] rounded-full flex justify-center pt-2">
              <div className="w-1 h-2 bg-[#a78bfa]/50 rounded-full" />
            </div>
          </motion.div>
        </section>

        {/* Community Groups Section */}
        <section className="py-24 relative">
          {/* Background Effects */}
          <div className="absolute inset-0 gradient-glow" />
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-px bg-gradient-to-r from-transparent via-[rgba(167,139,250,0.3)] to-transparent" />
          
          <div className="container mx-auto px-6 lg:px-12 relative z-10">
            {/* Section Header */}
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <span className="text-[#a78bfa] text-sm font-medium tracking-widest uppercase mb-4 block">Our Community</span>
              <h2 className="text-4xl md:text-5xl font-azonix font-bold mb-4">
                Find Your <span className="gradient-text">Tribe</span>
              </h2>
              <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                Six specialized communities, one united family. Find your space to connect, share, and grow with like-minded enthusiasts.
              </p>
            </motion.div>

            {/* Community Groups Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {communityGroups.map((group, index) => (
                <motion.div
                  key={group.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  onClick={() => setSelectedGroup(group)}
                  className="group relative cursor-pointer"
                >
                  {/* Glass Card */}
                  <div className="relative overflow-hidden rounded-2xl glass-card backdrop-blur-xl hover:border-[rgba(167,139,250,0.3)] transition-all duration-500 glow-border">
                    {/* Background Pattern */}
                    <div className="absolute inset-0 opacity-5 group-hover:opacity-10 transition-opacity">
                      <div className="absolute top-4 right-4 text-8xl transform rotate-12">{group.bgPattern}</div>
                    </div>
                    
                    {/* Gradient Border Effect on Hover */}
                    <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-br ${group.gradient} blur-xl`} />
                    
                    {/* Content */}
                    <div className="relative p-6">
                      {/* Icon */}
                      <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${group.gradient} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                        <group.icon className="text-white" size={28} />
                      </div>
                      
                      {/* Name & Tagline */}
                      <h3 className="text-xl font-azonix font-bold text-white mb-1 group-hover:text-white transition-colors">
                        {group.name}
                      </h3>
                      <p className={`text-sm ${group.accent} mb-3 opacity-80`}>{group.tagline}</p>
                      
                      {/* Description */}
                      <p className="text-gray-400 text-sm leading-relaxed mb-4 line-clamp-2">
                        {group.description}
                      </p>
                      
                      {/* Stats */}
                      <div className="flex gap-4 pt-4 border-t border-[rgba(167,139,250,0.15)]">
                        <div className="text-center">
                          <p className="text-lg font-bold text-white">{group.stats.members}</p>
                          <p className="text-xs text-gray-500">Members</p>
                        </div>
                        <div className="text-center">
                          <p className="text-lg font-bold text-white">{group.stats.posts}</p>
                          <p className="text-xs text-gray-500">Posts</p>
                        </div>
                        <div className="text-center">
                          <p className="text-lg font-bold text-green-400">{group.stats.online}</p>
                          <p className="text-xs text-gray-500">Online</p>
                        </div>
                      </div>
                      
                      {/* Learn More Arrow */}
                      <div className="absolute bottom-6 right-6 opacity-0 group-hover:opacity-100 transform translate-x-2 group-hover:translate-x-0 transition-all duration-300">
                        <ChevronRight className={group.accent} size={20} />
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Community Stats Banner */}
        <section className="py-16 relative">
          <div className="absolute inset-0 gradient-glow" />
          <div className="container mx-auto px-6 lg:px-12 relative z-10">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {[
                { value: '37K+', label: 'Total Members', icon: Users },
                { value: '243K+', label: 'Total Posts', icon: MessageCircle },
                { value: '2,340+', label: 'Online Now', icon: Sparkles },
                { value: '500+', label: 'Daily Active', icon: Trophy },
              ].map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center"
                >
                  <stat.icon className="mx-auto text-[#a78bfa] mb-3" size={24} />
                  <p className="text-3xl md:text-4xl font-azonix font-bold text-white mb-1">{stat.value}</p>
                  <p className="text-gray-500 text-sm">{stat.label}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 relative overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#a78bfa]/10 rounded-full blur-[150px]" />
          </div>
          <div className="container mx-auto px-6 lg:px-12 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-3xl mx-auto text-center"
            >
              <h2 className="text-4xl md:text-5xl font-azonix font-bold mb-6">
                Ready to <span className="gradient-text">Connect</span>?
              </h2>
              <p className="text-gray-400 text-lg mb-8 max-w-xl mx-auto">
                Join thousands of entertainment enthusiasts. Pick a community that matches your interests and start engaging today.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <button className="px-8 py-3.5 glass-card text-white font-semibold rounded-lg hover:bg-[rgba(167,139,250,0.2)] transition-all glow-border">
                  Join the Community
                </button>
                <Link to="/community/calendar" className="px-8 py-3.5 glass-card border border-[rgba(251,191,36,0.3)] text-yellow-400 font-semibold rounded-lg hover:bg-[rgba(251,191,36,0.1)] transition-all flex items-center gap-2">
                  <Calendar size={20} /> View Calendar
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Community Detail Modal */}
        <AnimatePresence>
          {selectedGroup && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-center justify-center p-4"
              onClick={() => setSelectedGroup(null)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.9, opacity: 0, y: 20 }}
                transition={{ type: "spring", damping: 25 }}
                className="glass-card backdrop-blur-xl rounded-3xl max-w-2xl w-full max-h-[85vh] overflow-y-auto glow-border"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header Banner */}
                <div className={`h-40 relative`} style={{
                  background: `linear-gradient(135deg, var(--${selectedGroup?.gradient || 'purple'}-600), var(--${selectedGroup?.gradient || 'purple'}-500))`
                }}>
                  <div className="absolute inset-0 bg-black/20" />
                  <div className="absolute -bottom-12 left-6 w-24 h-24 rounded-2xl glass-card backdrop-blur-xl border border-[rgba(255,255,255,0.2)] flex items-center justify-center">
                    <selectedGroup.icon className="text-white" size={40} />
                  </div>
                  <button
                    onClick={() => setSelectedGroup(null)}
                    className="absolute top-4 right-4 p-2 glass-card rounded-full hover:bg-[rgba(167,139,250,0.2)] transition-colors"
                  >
                    <X className="text-white" size={20} />
                  </button>
                </div>
                
                {/* Content */}
                <div className="pt-16 px-6 pb-8">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h2 className="text-2xl font-azonix font-bold text-white mb-1">{selectedGroup.name}</h2>
                      <p className={selectedGroup.accent}>{selectedGroup.tagline}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-white">{selectedGroup.stats.online}</p>
                      <p className="text-xs text-green-400">Online Now</p>
                    </div>
                  </div>
                  
                  <p className="text-gray-400 leading-relaxed mb-6">{selectedGroup.description}</p>
                  
                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    {Object.entries(selectedGroup.stats).map(([key, value]) => (
                      <div key={key} className="glass-card rounded-xl p-4 text-center border border-[rgba(167,139,250,0.15)]">
                        <p className="text-xl font-bold text-white">{value}</p>
                        <p className="text-xs text-gray-500 capitalize">{key}</p>
                      </div>
                    ))}
                  </div>
                  
                  {/* Features */}
                  <div className="mb-8">
                    <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">Key Features</h3>
                    <div className="flex flex-wrap gap-2">
                      {selectedGroup.features.map((feature, i) => (
                        <span 
                          key={i} 
                          className="px-3 py-1.5 glass-card rounded-full text-sm text-gray-300 border border-[rgba(167,139,250,0.15)]"
                        >
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex gap-3">
                    <button className={`flex-1 py-3 rounded-xl font-semibold bg-gradient-to-r ${selectedGroup.gradient} text-white hover:opacity-90 transition-opacity`}>
                      Join {selectedGroup.name}
                    </button>
                    <button className="px-4 py-3 glass-card border border-[rgba(167,139,250,0.3)] rounded-xl text-white/70 hover:bg-[rgba(167,139,250,0.1)] transition-colors">
                      <Instagram size={20} />
                    </button>
                    <button className="px-4 py-3 glass-card border border-[rgba(167,139,250,0.3)] rounded-xl text-white/70 hover:bg-[rgba(167,139,250,0.1)] transition-colors">
                      <Twitter size={20} />
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Learn More Modal */}
        <AnimatePresence>
          {learnMoreOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-center justify-center p-4"
              onClick={() => setLearnMoreOpen(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.9, opacity: 0, y: 20 }}
                transition={{ type: "spring", damping: 25 }}
                className="glass-card backdrop-blur-xl rounded-3xl max-w-2xl w-full max-h-[85vh] overflow-y-auto glow-border"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header Banner */}
                <div className="h-48 relative bg-gradient-to-br from-[#a78bfa] via-[#8b5cf6] to-[#6366f1]">
                  <div className="absolute inset-0 bg-black/20" />
                  <button
                    onClick={() => setLearnMoreOpen(false)}
                    className="absolute top-4 right-4 p-2 glass-card rounded-full hover:bg-[rgba(255,255,255,0.2)] transition-colors"
                  >
                    <X className="text-white" size={20} />
                  </button>
                </div>
                
                {/* Content */}
                <div className="pt-12 px-6 pb-8">
                  <h2 className="text-2xl font-azonix font-bold text-white mb-2">
                    {slideContent[activeSlide].title}
                  </h2>
                  <p className="text-gray-400 text-sm mb-6">Explore this community</p>
                  
                  <p className="text-gray-300 leading-relaxed mb-6">
                    {slideContent[activeSlide].description}
                  </p>
                  
                  {/* Features */}
                  <div className="mb-8">
                    <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">What You'll Find</h3>
                    <div className="flex flex-wrap gap-2">
                      {slideContent[activeSlide].features.map((feature, i) => (
                        <span 
                          key={i} 
                          className="px-3 py-1.5 glass-card rounded-full text-sm text-gray-300 border border-[rgba(167,139,250,0.15)]"
                        >
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex gap-3">
                    <button 
                      onClick={() => {
                        setLearnMoreOpen(false);
                        setJoinNowOpen(true);
                      }}
                      className="flex-1 py-3 rounded-xl font-semibold bg-gradient-to-r from-[#a78bfa] via-[#8b5cf6] to-[#6366f1] text-white hover:opacity-90 transition-opacity"
                    >
                      Join Now
                    </button>
                    <button 
                      onClick={() => window.open(mainCommunityLink, '_blank')}
                      className="px-6 py-3 glass-card border border-[rgba(167,139,250,0.3)] rounded-xl text-white/70 hover:bg-[rgba(167,139,250,0.1)] transition-colors"
                    >
                      Main Community
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Join Now Modal */}
        <AnimatePresence>
          {joinNowOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-center justify-center p-4"
              onClick={() => setJoinNowOpen(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.9, opacity: 0, y: 20 }}
                transition={{ type: "spring", damping: 25 }}
                className="glass-card backdrop-blur-xl rounded-3xl max-w-lg w-full glow-border"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div className="p-6 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-[#25D366] to-[#20BD5A] flex items-center justify-center">
                    <MessageCircle className="text-white" size={32} />
                  </div>
                  <h2 className="text-2xl font-azonix font-bold text-white mb-2">
                    Join Our {slideContent[activeSlide].title} Group
                  </h2>
                  <p className="text-gray-400 text-sm">
                    Connect with thousands of {slideContent[activeSlide].title.toLowerCase().split(' ')[0]} enthusiasts!
                  </p>
                </div>
                
                {/* Content */}
                <div className="px-6 pb-6">
                  <div className="glass-card rounded-xl p-4 mb-6 border border-[rgba(37,211,102,0.3)]">
                    <p className="text-gray-300 text-sm text-center">
                      Click the button below to join our WhatsApp community and start connecting with others who share your interests!
                    </p>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex flex-col gap-3">
                    <a 
                      href={slideContent[activeSlide].joinLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full py-4 rounded-xl font-semibold bg-gradient-to-r from-[#25D366] to-[#20BD5A] text-white hover:opacity-90 transition-opacity text-center flex items-center justify-center gap-2"
                    >
                      <MessageCircle size={20} />
                      Join WhatsApp Group
                    </a>
                    <button 
                      onClick={() => window.open(mainCommunityLink, '_blank')}
                      className="w-full py-3 glass-card border border-[rgba(167,139,250,0.3)] rounded-xl text-white/70 hover:bg-[rgba(167,139,250,0.1)] transition-colors text-sm"
                    >
                      View Main Community
                    </button>
                    <button 
                      onClick={() => setJoinNowOpen(false)}
                      className="w-full py-3 text-gray-500 hover:text-white transition-colors text-sm"
                    >
                      Maybe Later
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
}
